import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  Star, MessageSquare, Loader2, TrendingUp, Users, Calendar, EyeOff
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { GustoLogo, GustoIcon } from "@/components/GustoLogo";

const MyRatingsPage = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/ratings/received`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const ratings = data?.ratings || [];
  const avg = data?.average_rating || 0;
  const total = data?.total_count || 0;

  // Group by recipe
  const byRecipe = {};
  ratings.forEach((r) => {
    const key = r.recipe_id || r.recipe_name;
    if (!byRecipe[key]) {
      byRecipe[key] = { name: r.recipe_name, id: r.recipe_id, ratings: [] };
    }
    byRecipe[key].ratings.push(r);
  });

  // Stars distribution
  const dist = [0, 0, 0, 0, 0];
  ratings.forEach((r) => { if (r.rating >= 1 && r.rating <= 5) dist[r.rating - 1]++; });

  return (
    <div className="min-h-screen pb-24 px-4 pt-4" data-testid="my-ratings-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-1">
          <h1 className="font-heading text-2xl font-bold text-charcoal">Mes Notes</h1>
          <p className="text-sm text-muted-foreground">Les avis de vos convives</p>
        </div>

        {/* Summary Card */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-center gap-8">
                {/* Average */}
                <div className="text-center">
                  <p className="text-5xl font-black text-charcoal">{avg > 0 ? avg : "-"}</p>
                  <div className="flex justify-center gap-0.5 mt-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={`w-4 h-4 ${s <= Math.round(avg) ? "fill-amber-400 text-amber-400" : "text-gray-200"}`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">note moyenne</p>
                </div>
                {/* Stats */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-amber-500" />
                    <span className="font-medium">{total}</span>
                    <span className="text-muted-foreground">avis reçus</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <GustoIcon className="w-4 h-4" />
                    <span className="font-medium">{Object.keys(byRecipe).length}</span>
                    <span className="text-muted-foreground">plat(s) noté(s)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-amber-500" />
                    <span className="font-medium">{data?.invitations_count || 0}</span>
                    <span className="text-muted-foreground">invitation(s)</span>
                  </div>
                </div>
              </div>

              {/* Stars distribution */}
              {total > 0 && (
                <div className="mt-5 space-y-1.5">
                  {[5, 4, 3, 2, 1].map((s) => {
                    const count = dist[s - 1];
                    const pct = total > 0 ? (count / total) * 100 : 0;
                    return (
                      <div key={s} className="flex items-center gap-2">
                        <span className="text-xs w-3 text-right text-muted-foreground">{s}</span>
                        <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                        <div className="flex-1 h-2 bg-white/80 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full bg-amber-400 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${pct}%` }}
                            transition={{ duration: 0.8, delay: (5 - s) * 0.1 }}
                          />
                        </div>
                        <span className="text-xs w-6 text-muted-foreground">{count}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Ratings by Recipe */}
        {Object.entries(byRecipe).map(([key, group], gi) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + gi * 0.05 }}
          >
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <GustoIcon className="w-4 h-4" />
                  {group.name}
                  <Badge variant="outline" className="text-xs">{group.ratings.length} avis</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 space-y-3">
                {group.ratings.map((r, ri) => (
                  <div key={r.id} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${r.is_anonymous ? "bg-gradient-to-br from-gray-200 to-gray-300" : "bg-gradient-to-br from-amber-100 to-orange-100"}`}>
                      {r.is_anonymous ? (
                        <EyeOff className="w-4 h-4 text-gray-500" />
                      ) : (
                        <span className="text-sm font-bold text-amber-600">
                          {r.guest_name?.charAt(0)?.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm text-charcoal">
                          {r.is_anonymous ? "Anonyme" : r.guest_name}
                        </p>
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <Star
                              key={s}
                              className={`w-3 h-3 ${s <= r.rating ? "fill-amber-400 text-amber-400" : "text-gray-200"}`}
                            />
                          ))}
                        </div>
                      </div>
                      {r.comment && (
                        <p className="text-sm text-muted-foreground mt-1 italic">"{r.comment}"</p>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {new Date(r.created_at).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        ))}

        {total === 0 && (
          <div className="text-center py-12">
            <Star className="w-14 h-14 mx-auto text-muted-foreground/20 mb-4" />
            <p className="text-muted-foreground mb-2">Aucune note reçue pour le moment</p>
            <p className="text-sm text-muted-foreground">
              Invitez vos convives à noter vos plats après un repas !
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyRatingsPage;
